import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes } from '@/core/time/format';
import { cn } from '@/lib/cn';
import { summarize } from '@/lib/network-ledger';
import { useLedgerStore } from '@/state/ledger-store';
import { Popover } from './Popover';

/**
 * Always-visible proof of the privacy claim: bytes uploaded during this visit,
 * measured in the page and every worker. Opens to show what was downloaded.
 */
export function PrivacyLedger({ compact = false }: { readonly compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const entries = useLedgerStore((state) => state.entries);
  const summary = useMemo(() => summarize(entries), [entries]);
  const clean = summary.uploadedBytes === 0 && summary.unknownUploads === 0;
  const uploaded = formatBytes(summary.uploadedBytes, i18n.language);
  const thisHost = typeof location === 'undefined' ? '' : location.host;

  return (
    <Popover
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-label={`${t('ledger.title')}: ${t('ledger.chip', { size: uploaded })}`}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-xl px-2.5 text-[13px] font-medium tabular transition-colors',
            clean ? 'text-fg-muted hover:bg-elevated hover:text-fg' : 'bg-danger/10 text-danger',
          )}
        >
          {clean ? <ShieldCheck size={16} className="text-accent-text" /> : <ShieldAlert size={16} />}
          {/* Phones show just the figure; the full sentence is in the label and the popover. */}
          {!compact && <span className="sm:hidden">{uploaded}</span>}
          <span className="hidden sm:inline">{t('ledger.chip', { size: uploaded })}</span>
        </button>
      )}
    >
      {() => (
        <div className="space-y-4 p-3">
          <p className="text-sm font-semibold text-fg">{t('ledger.title')}</p>
          <dl className="grid grid-cols-2 gap-2">
            <div className={cn('rounded-xl px-3.5 py-3 ring-1 ring-line', clean ? 'bg-accent-soft' : 'bg-danger/10')}>
              <dt className="text-xs text-fg-muted">{t('ledger.uploaded')}</dt>
              <dd className={cn('mt-0.5 text-xl font-semibold tabular', clean ? 'text-fg' : 'text-danger')}>{uploaded}</dd>
            </div>
            <div className="rounded-xl bg-elevated px-3.5 py-3 ring-1 ring-line">
              <dt className="text-xs text-fg-muted">{t('ledger.downloaded')}</dt>
              <dd className="mt-0.5 text-xl font-semibold text-fg tabular">{formatBytes(summary.downloadedBytes, i18n.language)}</dd>
            </div>
          </dl>
          {!clean && <p className="text-[13px] leading-snug text-danger">{t('ledger.unexpected')}</p>}
          {summary.byHost.length === 0 ? (
            <p className="text-[13px] text-fg-muted">{t('ledger.nothingYet')}</p>
          ) : (
            <ul className="divide-y divide-line text-[13px]">
              {summary.byHost.map((host) => (
                <li key={host.host} className="flex items-center justify-between gap-3 py-2">
                  <span className="truncate text-fg">{host.host === thisHost ? t('ledger.thisSite') : host.host}</span>
                  <span className="shrink-0 text-fg-muted tabular">{formatBytes(host.downloaded, i18n.language)}</span>
                </li>
              ))}
            </ul>
          )}
          {summary.analytics.requests > 0 && (
            <div className="flex items-start justify-between gap-3 rounded-xl bg-elevated px-3.5 py-3 ring-1 ring-line">
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-fg">{t('ledger.analytics')}</p>
                <p className="mt-0.5 text-xs leading-snug text-fg-muted">{t('ledger.analyticsNote')}</p>
              </div>
              <span className="shrink-0 text-[13px] text-fg-muted tabular">{formatBytes(summary.analytics.sentBytes, i18n.language)}</span>
            </div>
          )}
          <p className="text-[13px] leading-relaxed text-fg-muted">{t('ledger.explain')}</p>
          <p className="text-[13px] leading-relaxed text-fg-subtle">{t('ledger.verify')}</p>
        </div>
      )}
    </Popover>
  );
}
